import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  type CSSProperties,
  type Ref
} from 'vue'

export interface UseDropdownViewportMaxHeightOptions {
  /** Gap between panel bottom and viewport edge (px). */
  bottomPadding?: number
}

/**
 * Constrains a floating panel so its max-height does not extend past the
 * bottom of the viewport, using the panel’s top edge.
 *
 * Exposes `maxHeightStyle` (bind to `:style` on the element that should shrink)
 *
 * Pair `begin()` / `end()` with show/hide of the overlay. Listeners are only
 * attached while active.
 */
export function useDropdownViewportMaxHeight(
  targetRef: Ref<HTMLElement | null>,
  options: UseDropdownViewportMaxHeightOptions = {}
) {
  const bottomPadding = options.bottomPadding ?? 12

  const maxHeightPx = ref<number | null>(null)

  let active = false
  let listenersAttached = false

  const maxHeightStyle = computed((): CSSProperties => {
    return maxHeightPx.value != null
      ? { maxHeight: `${maxHeightPx.value}px` }
      : {}
  })

  function measure() {
    if (!active) return
    const el = targetRef.value
    if (!el?.getBoundingClientRect) return
    const { top } = el.getBoundingClientRect()
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight
    const available = viewportHeight - top - bottomPadding
    maxHeightPx.value = Math.max(0, Math.floor(available))
  }

  function attachListeners() {
    if (listenersAttached) return
    listenersAttached = true
    window.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('resize', measure)
  }

  function detachListeners() {
    if (!listenersAttached) return
    listenersAttached = false
    window.removeEventListener('resize', measure)
    window.visualViewport?.removeEventListener('resize', measure)
  }

  function end() {
    active = false
    detachListeners()
    maxHeightPx.value = null
  }

  function begin() {
    active = true
    nextTick(() => {
      requestAnimationFrame(() => {
        if (!active) return
        measure()
        attachListeners()
      })
    })
  }

  onBeforeUnmount(end)

  return { maxHeightStyle, begin, end }
}
